import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, MailCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/apiClient";
import { attachFunnel } from "../lib/analytics";
import { toast } from "sonner";

export default function VerifyEmail() {
  const { verifyEmail, resendCode } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState(location.state?.email || "");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await verifyEmail(email.trim().toLowerCase(), code.trim());
      await attachFunnel();
      toast.success("Compte vérifié ! Bienvenue 🎉");
      navigate(user.onboarded ? "/dashboard" : "/onboarding");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    if (!email) return setError("Renseigne ton email.");
    setResending(true);
    try { await resendCode(email.trim().toLowerCase()); toast.success("Nouveau code envoyé par email"); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || "Échec de l'envoi"); }
    finally { setResending(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0C] flex items-center justify-center px-4 py-10 relative overflow-hidden">
      <div className="absolute -top-40 -left-40 w-96 h-96 rounded-full bg-amber-600/10 blur-3xl" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md relative z-10">
        <div className="flex items-center gap-2 justify-center mb-8">
          <img src={process.env.PUBLIC_URL + "/cehnura-mark.png"} alt="Cehnura" className="w-11 h-11 object-contain" />
          <span className="font-display font-extrabold text-2xl">Cehn<span className="gold-text">ura</span></span>
        </div>
        <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-8" data-testid="verify-card">
          <div className="flex items-center gap-2 mb-2"><MailCheck className="w-5 h-5 text-amber-400" /><h1 className="font-display text-2xl font-bold">Vérifie ton email</h1></div>
          <p className="text-sm text-zinc-500 mb-6">On t'a envoyé un code à 6 chiffres. Saisis-le pour activer ton compte.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-zinc-400 mb-1.5">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="verify-email" required
                className="w-full h-12 px-4 rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-100 placeholder-zinc-600 focus:border-amber-500 outline-none" placeholder="ton@email.com" />
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-400 mb-1.5">Code de vérification</label>
              <input inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} data-testid="verify-code" required
                className="w-full h-14 px-4 rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-100 text-center text-2xl tracking-[0.5em] focus:border-amber-500 outline-none" placeholder="000000" />
            </div>
            {error && <p className="text-sm text-red-400" data-testid="verify-error">{error}</p>}
            <button type="submit" disabled={loading} data-testid="verify-submit" className="w-full h-12 rounded-xl gold-gradient text-black font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Vérifier mon compte"}
            </button>
          </form>
          <button onClick={resend} disabled={resending} data-testid="verify-resend" className="w-full mt-4 text-sm text-amber-400 hover:underline disabled:opacity-50">
            {resending ? "Envoi…" : "Renvoyer le code"}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
